import project from "../../project.json"

/**
 * The app's view of project.json — MTC-FCS-030.
 * ============================================================================
 * `project.json` is the one file that says which project this is: where
 * accounts live and what is being sold. The setup scripts read it directly;
 * everything inside `src/` reads it through here, so there is a single place
 * that knows the path and a single place to look when it moves.
 *
 * ── WHY THE APP READS IT AT ALL ────────────────────────────────────────────
 * Because the alternative is the same numbers typed twice. The amount Stripe
 * charges is created from this file by `npm run setup:stripe`; if the pricing
 * table got its numbers from somewhere else, the first change to either would
 * put a different price on the screen from the one on the card statement.
 *
 * ── WHAT IS SAFE IN A BROWSER BUNDLE ───────────────────────────────────────
 * Everything here. Price ids and product ids are public by design — Stripe's
 * own client integrations put price ids in page source — and there is no key,
 * token or secret in project.json. Secrets live in .env, which is gitignored
 * and never imported by browser code.
 */

/** Which account table the app talks to unless the environment overrides it. */
export const ACCOUNTS_TABLE = project.accounts?.table || "test_users"

/** The live table, for the one switch that flips everything over. */
export const ACCOUNTS_LIVE_TABLE = project.accounts?.liveTable || "users"

/** Who may use the admin screens (FCS-073): the live table of administrators. */
export const ADMINS_LIVE_TABLE = project.admins?.liveTable || "admins"

/** Which site table the app talks to unless the environment overrides it. */
export const SITES_TABLE = project.sites?.table || "test_sites"
export const SITES_LIVE_TABLE = project.sites?.liveTable || "sites"

/** Affiliate tracking codes — MTC-FCS-041. */
export const LINKS_TABLE = project.affiliates?.table || "test_affiliate_links"
export const LINKS_LIVE_TABLE = project.affiliates?.liveTable || "affiliate_links"
export const EVENTS_TABLE = project.affiliates?.eventsTable || "test_affiliate_events"
export const EVENTS_LIVE_TABLE = project.affiliates?.eventsLiveTable || "affiliate_events"

/** Where uploaded images go — MTC-FCS-032. */
export const IMAGE_BUCKET = project.sites?.bucket || "site-images"

export const CURRENCY = (project.billing?.currency || "usd").toLowerCase()
export const INTERVAL = project.billing?.interval || "month"

/**
 * The paid plans, cheapest first, exactly as project.json lists them.
 *
 * `amount` is the CUMULATIVE price of the tier in cents — what Stripe charges.
 * The per-level difference the pricing table shows is worked out from it (see
 * src/data/plans.js) rather than stored, so the two cannot disagree.
 */
export const PAID_PLANS = (project.billing?.plans || []).map(plan => ({
  key: plan.key,
  tier: plan.tier,
  label: plan.label,
  summary: plan.summary || "",
  amount: plan.amount,
  interval: plan.interval || INTERVAL,
  currency: CURRENCY,
  envVar: plan.envVar || null,
}))

/** One plan by its key, or null. Never a guess. */
export function planByKey(key) {
  return PAID_PLANS.find(p => p.key === key) || null
}

export const PROJECT_ID = project.id
export const PROJECT_NAME = project.name

export default project
